/*
 * Marketing (draft 6c): saved client lists and the complaints log, with links to the automatic messages and the website
 * settings. The Marketing suite (draft 12: Google profile, ranks, ads, social, cold email) is in MarketingSuite.tsx, one
 * tab each, for those who may read marketing. The side menu groups the tabs. Each tab opens with the connections it needs
 * (MarketingConnect.tsx) and the menu counts what is left on each. The open tab lives in the address (#ranks), so it can be
 * linked.
 */
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import type { SmartFilter } from "@steward/shared";
import {
  useAddComplaint,
  useAddSmartList,
  useChangeComplaint,
  useClients,
  useComplaints,
  useSmartListClients,
  useSmartLists,
} from "../api/hooks";
import { useSession } from "../app/session";
import { Choice, Combobox, InputText } from "../components/fields";
import { plural, shortDate } from "../components/format";
import { Icon, type IconName } from "../components/Icon";
import { useToast } from "../components/overlays";
import { Button, Card, Page, Skeleton, StatusLabel } from "../components/parts";
import { errText } from "../components/work";
import { SuiteTabBody, type SuiteTab } from "./MarketingSuite";
import { Campaigns, CampaignLines } from "./Campaigns";
import { NeedsYou, openOn, TabConnections, useConnectSteps } from "./MarketingConnect";
import { SigninGate, SigninReturn } from "./MarketingSignin";
import { Dashboard, isMoreTab, MoreTabBody, type MoreTab } from "./MarketingTabs";
import { SettingsButton } from "./settings/Feature";

const PLAN_WORDS = { any: "Any plan", one_time: "One-time only", repeating: "On a repeating plan" } as const;

/** The side menu, grouped by what each part is for (2026-10-30: one row of 12 tabs was too much to read). */
type MkTab = SuiteTab | MoreTab | "lists" | "campaigns";
const MENU: [string | null, [MkTab, string, IconName][]][] = [
  [null, [["dash", "Overview", "home"]]],
  [
    "Get found",
    [
      ["profile", "Google profile", "pin"],
      ["ranks", "Website rank", "insights"],
      ["pitches", "Guest posts", "note"],
    ],
  ],
  [
    "Get new clients",
    [
      ["ads", "Ads", "activity"],
      ["social", "Social posts", "camera"],
      ["email", "Cold email", "mail"],
      ["signups", "Email list", "list"],
    ],
  ],
  [
    "Bring clients back",
    [
      ["campaigns", "Campaigns", "repeat"],
      ["due", "Due again", "clock"],
      ["lists", "Client lists", "person"],
    ],
  ],
  ["Results", [["channels", "Where clients come from", "money"]]],
];
const TABS = new Set<string>(MENU.flatMap(([, items]) => items.map(([v]) => v)));

export function Marketing() {
  const { can } = useSession();
  const navigate = useNavigate();
  const hash = useRouterState({ select: (s) => s.location.hash });
  const tab = (TABS.has(hash) ? hash : "dash") as MkTab;
  const setTab = (t: string) => {
    void navigate({ to: "/marketing", hash: t, hashScrollIntoView: false });
    window.scrollTo({ top: 0 });
  };
  const suite = can("marketing.read");
  if (!suite)
    return (
      <Page title="Marketing">
        <Lists />
      </Page>
    );
  return (
    <Page title="Marketing" secondaryAction={<SettingsButton to="/marketing/settings">Marketing settings</SettingsButton>}>
      <div className="settings-layout">
        <aside className="settings-side">
          <MarketingMenu tab={tab} onTab={setTab} />
        </aside>
        <div className="stack settings-main">
          <SigninReturn onTab={setTab} />
          <SigninGate tab={tab}>
            {tab === "dash" ? null : <TabConnections tab={tab} onTab={setTab} />}
            {tab === "lists" ? (
              <Lists />
            ) : tab === "campaigns" ? (
              <Campaigns />
            ) : tab === "dash" ? (
              <>
                <NeedsYou onTab={setTab} />
                <Dashboard onTab={setTab} campaigns={<CampaignLines onOpen={() => setTab("campaigns")} />} />
              </>
            ) : isMoreTab(tab) ? (
              <MoreTabBody tab={tab} onTab={setTab} />
            ) : (
              <SuiteTabBody tab={tab} />
            )}
          </SigninGate>
        </div>
      </div>
    </Page>
  );
}

function MarketingMenu({ tab, onTab }: { tab: MkTab; onTab: (t: string) => void }) {
  const { steps } = useConnectSteps(onTab);
  return (
    <nav className="subnav" aria-label="Marketing">
      {MENU.map(([group, items]) => (
        <div key={group ?? "top"} className="subnav-group">
          {group ? <span className="subnav-title">{group}</span> : null}
          {items.map(([v, label, icon]) => {
            const open = openOn(steps, v);
            return (
              <button
                key={v}
                type="button"
                className="subnav-btn"
                aria-current={tab === v ? "page" : undefined}
                data-status={tab === v ? "active" : undefined}
                onClick={() => onTab(v)}
              >
                <Icon name={icon} size={16} />
                {label}
                {open ? (
                  <span className="subnav-count" aria-label={`${plural(open, "thing")} to set up`}>
                    {open}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

function Lists() {
  const { can } = useSession();
  return (
    <>
      <div className="stack">
        <Card title="Automatic messages" action={<Link to="/settings/automations">Open</Link>}>
          <p className="muted">
            Reminders, follow-ups, review requests and referral links. The past client texts are under Campaigns.
          </p>
        </Card>
        <SmartLists canSave={can("client.bulk")} />
        <Complaints canChange={can("client.update")} />
        {can("settings.read") ? (
          <Card title="Website" action={<Link to="/requests/settings">Open</Link>}>
            <p className="muted">Which websites send requests here, the ZIP codes we serve, and signups.</p>
          </Card>
        ) : null}
      </div>
    </>
  );
}

function SmartLists({ canSave }: { canSave: boolean }) {
  const lists = useSmartLists();
  const add = useAddSmartList();
  const toast = useToast();
  const [open, setOpen] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    name: string;
    plan: SmartFilter["plan"];
    status: SmartFilter["status"];
    days: string;
  } | null>(null);
  return (
    <Card
      title="Saved client lists"
      flush
      action={
        canSave && !draft ? (
          <Button
            type="secondary"
            size="small"
            icon="plus"
            onClick={() => setDraft({ name: "", plan: "one_time", status: "active", days: "60" })}
          >
            New list
          </Button>
        ) : null
      }
    >
      {draft ? (
        <div className="stack auto-step">
          <InputText label="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          <Choice
            label="Plan"
            value={draft.plan}
            options={Object.entries(PLAN_WORDS).map(([value, label]) => ({ value: value as SmartFilter["plan"], label }))}
            onChange={(plan) => setDraft({ ...draft, plan })}
          />
          <Choice
            label="Clients"
            value={draft.status}
            options={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
              { value: "all", label: "Everyone" },
            ]}
            onChange={(status) => setDraft({ ...draft, status })}
          />
          <InputText
            label="Last visit at least this many days ago"
            inputMode="numeric"
            value={draft.days}
            onChange={(e) => setDraft({ ...draft, days: e.target.value.replace(/\D/g, "") })}
          />
          <div className="row end">
            <Button type="tertiary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={add.isPending}
              onClick={() =>
                add.mutate(
                  {
                    name: draft.name,
                    filter: {
                      plan: draft.plan,
                      status: draft.status,
                      tags: [],
                      zips: [],
                      lastVisitMinDays: draft.days ? Number(draft.days) : null,
                      lastVisitMaxDays: null,
                      marketingOnly: true,
                    },
                  },
                  { onSuccess: () => setDraft(null), onError: (e) => toast(errText(e), { kind: "error" }) },
                )
              }
            >
              Save list
            </Button>
          </div>
        </div>
      ) : null}
      {!lists.data ? (
        <Skeleton rows={2} />
      ) : lists.data.lists.length === 0 ? (
        <p className="muted auto-intro">No saved lists yet.</p>
      ) : (
        <ul className="auto-list">
          {lists.data.lists.map((l) => (
            <li key={l.id}>
              <button
                type="button"
                className="setting-row"
                onClick={() => setOpen(open === l.id ? null : l.id)}
                aria-expanded={open === l.id}
              >
                <span className="what">
                  <b>{l.name}</b>
                  <span>
                    {PLAN_WORDS[l.filter.plan]}
                    {l.filter.lastVisitMinDays !== null ? `, last visit ${String(l.filter.lastVisitMinDays)}+ days ago` : ""}
                  </span>
                </span>
                <StatusLabel status="informative">{`${String(l.count)} clients`}</StatusLabel>
              </button>
              {open === l.id ? <ListClients id={l.id} /> : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ListClients({ id }: { id: string }) {
  const c = useSmartListClients(id);
  if (!c.data) return <Skeleton rows={2} />;
  return (
    <ul className="auto-list">
      {c.data.clients.slice(0, 100).map((x) => (
        <li key={x.id} className="setting-row">
          <Link to="/clients/$id" params={{ id: x.id }}>
            {x.name}
          </Link>
          <span className="muted">{x.lastVisit ? `Last visit ${shortDate(x.lastVisit)}` : "No visits yet"}</span>
        </li>
      ))}
    </ul>
  );
}

function Complaints({ canChange }: { canChange: boolean }) {
  const { company } = useSession();
  const list = useComplaints();
  const add = useAddComplaint();
  const change = useChangeComplaint();
  const toast = useToast();
  const [draft, setDraft] = useState<{ clientId: string; what: string; followUpOn: string } | null>(null);
  const clients = useClients(company.id, {}, draft !== null);
  return (
    <Card
      title="Complaints"
      flush
      action={
        canChange && !draft ? (
          <Button type="secondary" size="small" icon="plus" onClick={() => setDraft({ clientId: "", what: "", followUpOn: "" })}>
            Log a complaint
          </Button>
        ) : null
      }
    >
      {draft ? (
        <div className="stack auto-step">
          <Combobox
            label="Client"
            value={draft.clientId}
            onChange={(clientId) => setDraft({ ...draft, clientId })}
            options={(clients.data?.items ?? []).map((c) => ({ value: c.id, label: c.name }))}
          />
          <label className="field wide">
            <span className="label">What went wrong</span>
            <textarea rows={3} value={draft.what} onChange={(e) => setDraft({ ...draft, what: e.target.value })} />
          </label>
          <InputText
            label="Follow up on"
            type="date"
            value={draft.followUpOn}
            onChange={(e) => setDraft({ ...draft, followUpOn: e.target.value })}
          />
          <div className="row end">
            <Button type="tertiary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={add.isPending}
              onClick={() =>
                add.mutate(
                  { clientId: draft.clientId, what: draft.what, followUpOn: draft.followUpOn || null },
                  { onSuccess: () => setDraft(null), onError: (e) => toast(errText(e), { kind: "error" }) },
                )
              }
            >
              Save
            </Button>
          </div>
        </div>
      ) : null}
      {!list.data ? (
        <Skeleton rows={2} />
      ) : list.data.complaints.length === 0 ? (
        <p className="muted auto-intro">No open complaints.</p>
      ) : (
        <ul className="auto-list">
          {list.data.complaints.map((c) => (
            <li key={c.id} className="setting-row">
              <span className="what">
                <b>{c.clientName}</b>
                <span>
                  {c.what}
                  {c.followUpOn ? ` Follow up ${shortDate(c.followUpOn)}.` : ""}
                </span>
              </span>
              {canChange ? (
                <Button
                  type="tertiary"
                  size="small"
                  loading={change.isPending}
                  onClick={() => change.mutate({ id: c.id, patch: { status: "closed" } }, { onSuccess: () => toast("Closed") })}
                >
                  Close
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
